import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import VideoViewer from './VideoViewer';
import LocationForm from './LocationForm';
import useServerUrl from '../hooks/useServerUrl';
import './LocationVideoViewer.css';

interface Location {
  id: string;
  name: string;
  description?: string;
  locationKey: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const LocationVideoViewer: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { theme } = useTheme();
  const SERVER_URL = useServerUrl(); // Runtime config
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLocationForm, setShowLocationForm] = useState(false);

  useEffect(() => {
    if (locationId) {
      fetchLocation();
    }
  }, [locationId, token]);

  const fetchLocation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${SERVER_URL}/api/locations/${locationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLocation(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch location');
      console.error('Error fetching location:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleEditLocation = () => {
    setShowLocationForm(true);
  };

  const handleLocationFormClose = () => {
    setShowLocationForm(false);
    // Refresh location data after edit
    if (locationId) {
      fetchLocation();
    }
  };

  if (loading) {
    return (
      <div className="location-video-viewer">
        <div className="loading">Loading location...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="location-video-viewer">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBackToHome} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="location-video-viewer">
        <div className="error">
          <h2>Location Not Found</h2>
          <p>The requested location could not be found.</p>
          <button onClick={handleBackToHome} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Location Header */}
        <div className={`${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        } border rounded-2xl p-4 mb-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleBackToHome}
                className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </button>
              
              <div className="flex items-center space-x-3">
                <h1 className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {location.name}
                </h1>
                
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  location.isActive
                    ? theme === 'dark'
                      ? 'bg-green-900/50 text-green-400'
                      : 'bg-green-100 text-green-800'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-400'
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {location.isActive ? '🟢 Active' : '🔴 Inactive'}
                </span>
              </div>
            </div>
            
            <button
              onClick={handleEditLocation}
              className={`p-2 rounded-lg transition-all duration-200 ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="Edit Location"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
          
          {location.description && (
            <p className={`text-sm mt-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {location.description}
            </p>
          )}
        </div>
        
        {/* Camera Streams Section */}
        <div className={`${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        } border rounded-2xl p-4`}>
          <h2 className={`text-lg font-semibold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Camera Streams
          </h2>
          <VideoViewer locationKey={location.locationKey} />
        </div>
      </div>

      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationForm
          location={location}
          onClose={handleLocationFormClose}
          onSuccess={handleLocationFormClose}
        />
      )}
    </div>
  );
};

export default LocationVideoViewer;
